import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import { ApiMethods } from '@models/enums';
import {
  AccountingBrowseResponse,
  AccountingBrowseUpdateData,
  AccountingDetails,
  AccountingExportParams,
  AccountingFinancialTab,
  AccountingSummaryTab,
  PaginatedResponse,
} from '@models/interfaces';
import {
  AccountingFilterOptionsResponse,
  MainAccountingBrowseFilter,
} from '@models/types';
import {
  AccountingActions,
  BreadcrumbsActions,
  SOFToolActions,
} from '@store/actions';
import {
  AccountingEffects,
  ProjectFuzeIDsEffects,
  ProjectsEffects,
} from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class AccountingHttpService extends CommonHttpService {
  @StoredRequest(AccountingActions.getAccountingBrowseDataAction)
  getAccountingBrowse(
    params: MainAccountingBrowseFilter,
  ): Promise<PaginatedResponse<AccountingBrowseResponse>> {
    return this.http.callApi.get(`${this.apiUrl}/accounting`, null, {
      params,
    });
  }

  @StoredRequest(SOFToolActions.getSOFToolBrowseFilters)
  getAccountingBrowseFiltersOptions(): Promise<AccountingFilterOptionsResponse> {
    return this.http.callApi.get(`${this.apiUrl}/accounting/filter-options`);
  }

  @StoredRequest(
    AccountingActions.getAccountingDetailsAction,
    BreadcrumbsActions.accounting,
  )
  async getAccounting(projectBundleID: string): Promise<AccountingDetails> {
    const resp = await this.http.callApi.get(
      `${this.apiUrl}/accounting/${projectBundleID}`,
    );

    ProjectsEffects.setPaceTasks(resp);

    ProjectsEffects.setHoldOptions(
      ProjectsEffects.setCurrentProvidenceAgent(
        ProjectFuzeIDsEffects.setProjectFuzeIDsFromDetails(resp.projectData),
      ),
    );

    return resp;
  }

  @StoredRequest(AccountingActions.updateAccountingBrowseAction)
  async updateAccountingBrowse(
    id: string,
    body: AccountingBrowseUpdateData,
  ): Promise<AccountingBrowseUpdateData> {
    await this.http.callApi.patch(`${this.apiUrl}/accounting`, body, {
      params: { projectId: id },
    });

    return { ...body, id };
  }

  @StoredRequest(
    AccountingActions.getAccountingSummaryTab,
    BreadcrumbsActions.accounting,
  )
  async getAccountingSummary(
    projectBundleID: string,
  ): Promise<AccountingSummaryTab> {
    const resp: AccountingSummaryTab = await this.http.callApi.get(
      `${this.apiUrl}/accounting/summary/${projectBundleID}`,
    );

    AccountingEffects.setPaceTasks(resp);
    AccountingEffects.setCurrentProvidenceAgent(resp.projectData);
    ProjectFuzeIDsEffects.setProjectFuzeIDs(resp.projectData.categories);

    return resp;
  }

  @StoredRequest(AccountingActions.getAccountingFinancialTab)
  async getAccountingFinancial(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<AccountingFinancialTab> {
    const response: AccountingFinancialTab = await this.http.callApi.get(
      `${this.apiUrl}/accounting/financial/${projectBundleID}`,
    );

    return response;
  }

  exportAccountingProjects({
    exportType,
    ...params
  }: AccountingExportParams): Promise<void> {
    return this.downloadFile(
      ApiMethods.Get,
      `${this.apiUrl}/accounting/report/${exportType.toLowerCase()}`,
      { params },
    );
  }
}
